/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Me · Home — today snapshot + 1-tap Punch In/Out
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Fingerprint,
  MapPin,
  Clock,
  CheckCircle2,
  CalendarOff,
  ClipboardList,
  Megaphone,
  ArrowRight,
  Wallet,
  Camera,
  X,
  ListChecks,
} from "lucide-react";

function fmtClock(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return "—"; }
}
function fmtMins(m) {
  if (!m && m !== 0) return "—";
  const h = Math.floor(m / 60), mm = m % 60;
  return `${h}h ${mm}m`;
}

export default function MeHome() {
  const [today, setToday] = useState(null);
  const [dash, setDash] = useState(null);
  const [taskCount, setTaskCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [actionPending, setActionPending] = useState(null);  // "in" | "out"

  const load = async () => {
    setLoading(true);
    try {
      const [t, d] = await Promise.all([
        api.get("/my-portal/punch/today"),
        api.get("/my-portal/dashboard").catch(() => ({ data: null })),
      ]);
      setToday(t.data);
      setDash(d.data);
      try {
        const tk = await api.get("/delegation/tasks", { params: { mode: "assignee", status: "open", limit: 50 } });
        setTaskCount((tk.data || []).length);
      } catch {/* */}
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rec = today?.record;
  const hasIn = !!rec?.punch_in_at;
  const hasOut = !!rec?.punch_out_at;

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  const requestPunch = async (action, lat, lng, label, selfieData) => {
    setBusy(true);
    try {
      await api.post("/my-portal/punch", {
        action, lat, lng, location_label: label,
        selfie_data_url: selfieData,
      });
      toast.success(action === "in" ? "Punched In ✓" : "Punched Out ✓");
      setSelfie(null);
      setSelfieOpen(false);
      setActionPending(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Punch failed");
    } finally { setBusy(false); }
  };

  const tryPunch = (action) => {
    setActionPending(action);
    if (!navigator.geolocation) {
      requestPunch(action, null, null, null, null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        requestPunch(action, pos.coords.latitude, pos.coords.longitude,
          `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
          selfie);
      },
      () => requestPunch(action, null, null, null, selfie),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 },
    );
  };

  const att = dash?.attendance || {};
  const lev = dash?.leave_balance || {};
  const slip = dash?.latest_payslip;
  const emp = dash?.employee || {};

  return (
    <div className="space-y-4" data-testid="me-home">
      {/* Greeting */}
      <div>
        <div className="text-[11px] text-slate-500">{greeting},</div>
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          {emp.name?.split(" ")[0] || today?.employee_name?.split(" ")[0] || "team"}
        </h1>
        <p className="text-[11px] text-slate-500">
          {emp.designation || ""}{emp.punch_id ? ` · ${emp.punch_id}` : ""}
        </p>
      </div>

      {/* Punch Hero Card */}
      <div className={`rounded-2xl p-4 shadow-sm ring-1 ${
        hasOut ? "bg-emerald-50 ring-emerald-200"
        : hasIn ? "bg-amber-50 ring-amber-200"
        : "bg-slate-900 ring-slate-900 text-white"
      }`} data-testid="me-home-punch-card">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className={`text-[10px] uppercase tracking-widest font-semibold ${
              hasOut ? "text-emerald-700"
              : hasIn ? "text-amber-700"
              : "text-red-300"
            }`}>
              {hasOut ? "Day complete" : hasIn ? "Currently working" : "Not punched in"}
            </div>
            <div className={`text-3xl font-bold ${
              hasOut ? "text-emerald-900"
              : hasIn ? "text-amber-900"
              : "text-white"
            }`}>
              {hasOut ? fmtClock(rec.punch_out_at)
                : hasIn ? fmtClock(rec.punch_in_at)
                : new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
            </div>
            {hasOut && rec.worked_minutes != null && (
              <div className="text-[11px] text-emerald-700 mt-0.5">
                Worked {fmtMins(rec.worked_minutes)} today
              </div>
            )}
          </div>
          <Fingerprint className={`h-7 w-7 ${
            hasOut ? "text-emerald-600"
            : hasIn ? "text-amber-600"
            : "text-red-300"
          }`} />
        </div>

        {hasIn && (
          <div className={`text-[11px] mb-3 ${
            hasOut ? "text-emerald-700" : "text-amber-700"
          }`}>
            <Clock className="h-3 w-3 inline mr-1" />
            In: <strong>{fmtClock(rec.punch_in_at)}</strong>
            {hasOut && <> · Out: <strong>{fmtClock(rec.punch_out_at)}</strong></>}
            {rec.punch_in_loc_label && (
              <div className="mt-0.5 flex items-center gap-1 text-[10px] opacity-80">
                <MapPin className="h-3 w-3" /> {rec.punch_in_loc_label}
              </div>
            )}
          </div>
        )}

        {/* Action button */}
        {loading ? (
          <div className="text-center py-3 text-xs opacity-70">Loading…</div>
        ) : !hasIn ? (
          <button
            disabled={busy}
            onClick={() => setSelfieOpen(true)}
            data-testid="me-home-punch-in"
            className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white py-4 font-bold text-base disabled:bg-emerald-300 flex items-center justify-center gap-2"
          >
            <Fingerprint className="h-5 w-5" /> {busy ? "Punching in…" : "Punch In"}
          </button>
        ) : !hasOut ? (
          <button
            disabled={busy}
            onClick={() => setSelfieOpen(true)}
            data-testid="me-home-punch-out"
            className="w-full rounded-xl bg-red-500 hover:bg-red-600 text-white py-4 font-bold text-base disabled:bg-red-300 flex items-center justify-center gap-2"
          >
            <Fingerprint className="h-5 w-5" /> {busy ? "Punching out…" : "Punch Out"}
          </button>
        ) : (
          <div className="w-full rounded-xl bg-emerald-100 text-emerald-800 py-3 font-semibold text-sm text-center">
            <CheckCircle2 className="h-4 w-4 inline mr-1" /> All done for today
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3">
        <KPI Icon={CheckCircle2} label="Attendance" value={`${att.attendance_pct ?? 0}%`}
             sub={`${att.present || 0}P · ${att.half || 0}H · ${att.absent || 0}A`} tone="emerald" />
        <KPI Icon={CalendarOff} label="Leave Balance"
             value={`EL ${Math.max(0, Number(lev.EL ?? 0))}`}
             sub={`PL ${Math.max(0, Number(lev.PL ?? 0))} remaining`} tone="amber" />
        <KPI Icon={ClipboardList} label="My Tasks" value={taskCount}
             sub={taskCount === 0 ? "All caught up" : "Open today"} tone="indigo" />
        <KPI Icon={Wallet} label="Last Payslip"
             value={slip ? "₹" + Math.round(Number(slip.net_salary || 0)).toLocaleString("en-IN") : "—"}
             sub={slip?.month || "No payslip"} tone="pink" />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-2">
        <QuickLink to="/me/tasks" label="View Tasks" icon={ClipboardList} />
        <QuickLink to="/my-portal/todos" label="My To-Dos" icon={ListChecks} />
        <QuickLink to="/me/leaves" label="Apply Leave" icon={CalendarOff} />
        <QuickLink to="/my-portal/announcements" label="Announcements" icon={Megaphone} />
        <QuickLink to="/my-portal/salary" label="Salary Slips" icon={Wallet} />
      </div>

      {/* Selfie Sheet */}
      {selfieOpen && (
        <SelfieSheet
          onConfirm={(img) => { setSelfie(img); tryPunch(actionPending ?? (hasIn ? "out" : "in")); }}
          onSkip={() => tryPunch(actionPending ?? (hasIn ? "out" : "in"))}
          onClose={() => { setSelfieOpen(false); setActionPending(null); }}
          action={hasIn && !hasOut ? "out" : "in"}
          busy={busy}
        />
      )}
    </div>
  );
}

function KPI({ Icon, label, value, sub, tone = "slate" }) {
  const tones = {
    emerald: "ring-emerald-100 bg-emerald-50/30 text-emerald-700",
    amber: "ring-amber-100 bg-amber-50/30 text-amber-700",
    indigo: "ring-indigo-100 bg-indigo-50/30 text-indigo-700",
    pink: "ring-red-100 bg-red-50/30 text-red-700",
    slate: "ring-slate-100 bg-white text-slate-700",
  };
  return (
    <div className={`rounded-xl ring-1 p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold opacity-80">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo" }}>{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function QuickLink({ to, label, icon: Icon }) {
  return (
    <Link to={to}
      data-testid={`me-quick-${to.replace(/\//g, "-")}`}
      className="rounded-xl ring-1 ring-slate-200 bg-white p-3 flex items-center gap-2 hover:ring-red-200 transition"
    >
      <div className="h-8 w-8 rounded-lg bg-red-50 brand-text flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <span className="text-xs font-semibold text-slate-700 flex-1">{label}</span>
      <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
    </Link>
  );
}

/* -------------------------------------------------------------------- */
/* Selfie capture (optional)                                              */
/* -------------------------------------------------------------------- */
function SelfieSheet({ onConfirm, onSkip, onClose, action, busy }) {
  const [stream, setStream] = useState(null);
  const [captured, setCaptured] = useState(null);
  const [videoRef, setVideoRef] = useState(null);

  useEffect(() => {
    if (videoRef && !stream) {
      navigator.mediaDevices?.getUserMedia?.({ video: { facingMode: "user" }, audio: false })
        .then((s) => { setStream(s); videoRef.srcObject = s; videoRef.play(); })
        .catch(() => { /* camera denied; user can still skip */ });
    }
    return () => { if (stream) stream.getTracks().forEach((t) => t.stop()); };
    // eslint-disable-next-line
  }, [videoRef]);

  const snap = () => {
    if (!videoRef) return;
    const c = document.createElement("canvas");
    c.width = 320; c.height = 240;
    c.getContext("2d").drawImage(videoRef, 0, 0, c.width, c.height);
    setCaptured(c.toDataURL("image/jpeg", 0.6));
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" data-testid="me-selfie-sheet">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold">Punch {action === "in" ? "In" : "Out"} · Selfie</h3>
          <button onClick={onClose} className="p-1 text-slate-500"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Optional selfie for verification. Tap "Skip selfie" if not needed.
        </p>
        <div className="rounded-xl overflow-hidden bg-black aspect-square mb-3 flex items-center justify-center">
          {captured ? (
            <img src={captured} alt="Selfie" className="w-full h-full object-cover" />
          ) : (
            <video ref={(r) => setVideoRef(r)} muted playsInline
                   className="w-full h-full object-cover" />
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {captured ? (
            <>
              <button onClick={() => { setCaptured(null);
                  navigator.mediaDevices?.getUserMedia?.({ video: { facingMode: "user" }, audio: false })
                    .then((s) => { setStream(s); if (videoRef) { videoRef.srcObject = s; videoRef.play(); }});
                }}
                className="rounded-xl ring-1 ring-slate-300 py-3 text-sm font-semibold text-slate-700"
                data-testid="me-selfie-retake"
              >
                Retake
              </button>
              <button onClick={() => onConfirm(captured)} disabled={busy}
                data-testid="me-selfie-confirm"
                className="rounded-xl bg-emerald-600 text-white py-3 text-sm font-bold disabled:bg-slate-300">
                {busy ? "Punching…" : "Confirm & Punch"}
              </button>
            </>
          ) : (
            <>
              <button onClick={onSkip} disabled={busy}
                data-testid="me-selfie-skip"
                className="rounded-xl ring-1 ring-slate-300 py-3 text-sm font-semibold text-slate-700">
                Skip selfie
              </button>
              <button onClick={snap}
                data-testid="me-selfie-snap"
                className="rounded-xl bg-slate-900 text-white py-3 text-sm font-bold flex items-center justify-center gap-2">
                <Camera className="h-4 w-4" /> Capture
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
